import {
  authCardSurfaceSx,
  authDescriptionSlate,
  authHeadingSlate,
  authOutlinedInputSurfaceSx,
  authTopBarBorder,
  borderInput,
  surfaceAuthCard,
  textPrimary,
} from '@/theme/designTokens'
import { loginInputSx } from '@/features/auth/styles/loginSplitStyles'
import {
  SURVEY_FIRST_LOGO_HEIGHT_PX,
  SURVEY_FIRST_LOGO_MAX_WIDTH_PX,
  SURVEY_FIRST_VERIFY_CARD_WIDTH_PX,
} from './instrumentOnboardingStyles'

/** Figma 144:6656 / 144:6665 — centered Create Account card. */
export const WEB_SURVEY_ACCOUNT_NAVY = '#122740'
export const WEB_SURVEY_ACCOUNT_ORANGE = '#FF9933'
export const WEB_SURVEY_ACCOUNT_SIGN_IN = '#F59E0B'
export const WEB_SURVEY_ACCOUNT_ASTERISK = '#FF5959'
export const WEB_SURVEY_ACCOUNT_FIELD_BORDER = '#E5E7EB'
export const WEB_SURVEY_LEGAL_ACCEPT_DISABLED_BG = '#FCE1C7'
export const WEB_SURVEY_LEGAL_ACCEPT_DISABLED_FG = 'rgba(18, 39, 64, 0.6)'

/** Figma 144:6665 — 574× card, heading/form inset 36px (not the phone-card 79px). */
export const webSurveyAccountPageSx = {
  ...authCardSurfaceSx,
  width: '100%',
  maxWidth: SURVEY_FIRST_VERIFY_CARD_WIDTH_PX,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'stretch',
  boxSizing: 'border-box',
  border: `1px solid ${authTopBarBorder}`,
  borderRadius: '12px',
  boxShadow: '0px 4px 2px rgba(0, 0, 0, 0.25)',
  p: { xs: 3, sm: '26px 36px 32px' },
  gap: 0,
  overflow: 'visible',
} as const

export const webSurveyAccountConsentRowSx = {
  width: '100%',
  display: 'flex',
  alignItems: 'flex-start',
  gap: '8px',
} as const

export const webSurveyAccountConsentTextSx = {
  fontFamily: 'Inter, sans-serif',
  fontWeight: 400,
  fontSize: '15px',
  lineHeight: '22.5px',
  color: '#0D1B2A',
  m: 0,
  pt: '9px',
} as const

export const webSurveyAccountConsentLinkSx = {
  color: WEB_SURVEY_ACCOUNT_SIGN_IN,
  fontWeight: 600,
  fontSize: '15px',
  lineHeight: '22.5px',
  textDecoration: 'underline',
  textUnderlineOffset: '2px',
  verticalAlign: 'baseline',
  p: 0,
  minWidth: 0,
  '&:hover': {
    color: WEB_SURVEY_ACCOUNT_SIGN_IN,
    textDecoration: 'underline',
    bgcolor: 'transparent',
  },
} as const

export const webSurveyLegalOverlayPaperSx = {
  width: { xs: 'calc(100% - 32px)', sm: 584, md: 672 },
  maxWidth: 672,
  height: { xs: 'min(508px, calc(100dvh - 32px))', sm: 508 },
  maxHeight: 'calc(100dvh - 32px)',
  m: 2,
  border: '1px solid #bfc7d2',
  borderRadius: '8px',
  boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.05)',
  overflow: 'hidden',
  bgcolor: '#ffffff',
} as const

export const webSurveyLegalAcceptDisabledSx = {
  bgcolor: WEB_SURVEY_LEGAL_ACCEPT_DISABLED_BG,
  color: WEB_SURVEY_LEGAL_ACCEPT_DISABLED_FG,
  '&.Mui-disabled': {
    bgcolor: WEB_SURVEY_LEGAL_ACCEPT_DISABLED_BG,
    color: WEB_SURVEY_LEGAL_ACCEPT_DISABLED_FG,
  },
} as const

export const webSurveyAccountFormSx = {
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'stretch',
  gap: '20px',
  boxSizing: 'border-box',
} as const

/** Figma 144:6694 — same lockup as the phone card (~199×57). */
export const webSurveyAccountLogoBlockSx = {
  height: { xs: 80, sm: SURVEY_FIRST_LOGO_HEIGHT_PX },
  minHeight: { xs: 80, sm: SURVEY_FIRST_LOGO_HEIGHT_PX },
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  mb: { xs: 2, sm: '28px' },
} as const

export const webSurveyAccountLogoSx = {
  width: 'auto',
  height: { xs: 80, sm: SURVEY_FIRST_LOGO_HEIGHT_PX },
  maxWidth: { xs: '100%', sm: SURVEY_FIRST_LOGO_MAX_WIDTH_PX },
  objectFit: 'contain',
  objectPosition: 'center',
  display: 'block',
  flexShrink: 0,
} as const

/** Figma 144:6744 — Inter SemiBold 32/56, tracking −3.0592, navy. */
export const webSurveyAccountHeadingSx = {
  fontFamily: 'Inter, sans-serif',
  fontWeight: 600,
  fontSize: { xs: '24px', sm: '32px' },
  lineHeight: { xs: '32px', sm: '56px' },
  letterSpacing: { xs: '-0.4px', sm: '-3.0592px' },
  color: WEB_SURVEY_ACCOUNT_NAVY,
  textAlign: { xs: 'center', sm: 'left' },
  m: 0,
  mt: { xs: 2, sm: '31px' },
  width: '100%',
} as const

/** Figma 144:6742 — 14/16, 456px, 46px above the form stack. */
export const webSurveyAccountSubtitleSx = {
  fontFamily: 'Inter, sans-serif',
  fontWeight: 400,
  fontSize: '14px',
  lineHeight: { xs: '20px', sm: '16px' },
  letterSpacing: '-0.4304px',
  color: authDescriptionSlate,
  textAlign: { xs: 'center', sm: 'left' },
  m: 0,
  mb: { xs: 3, sm: '46px' },
  width: '100%',
  maxWidth: 456,
  mx: { xs: 'auto', sm: 0 },
} as const


export const webSurveyAccountNameRowSx = {
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'flex-start',
  gap: { xs: '12px', sm: '20px' },
  width: '100%',
} as const

export const webSurveyAccountNameFieldSx = {
  minWidth: 0,
  flex: '1 1 0',
} as const

export const webSurveyAccountFieldBlockSx = {
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
} as const
export const webSurveyAccountLabelSx = {
  fontFamily: 'Inter, sans-serif',
  fontWeight: 500,
  fontSize: '16px',
  lineHeight: '20px',
  letterSpacing: '-0.9525px',
  color: authHeadingSlate,
  m: 0,
  display: 'block',
} as const

export const webSurveyAccountAsteriskSx = {
  color: WEB_SURVEY_ACCOUNT_ASTERISK,
} as const

export const webSurveyAccountInputSx = {
  ...loginInputSx,
  height: 50,
  borderRadius: '8px',
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: WEB_SURVEY_ACCOUNT_ORANGE,
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: WEB_SURVEY_ACCOUNT_ORANGE,
    borderWidth: '1px',
  },
} as const

export const webSurveyAccountPasswordInputSx = {
  ...authOutlinedInputSurfaceSx,
  height: 50,
  borderRadius: '10px',
  fontSize: '16px',
  letterSpacing: '-0.1504px',
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: WEB_SURVEY_ACCOUNT_FIELD_BORDER,
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: WEB_SURVEY_ACCOUNT_FIELD_BORDER,
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: WEB_SURVEY_ACCOUNT_ORANGE,
    borderWidth: '1px',
  },
  '& .MuiOutlinedInput-input, & .MuiInputBase-input': {
    px: '16px',
    py: '12px',
    background: surfaceAuthCard,
    backgroundColor: surfaceAuthCard,
    '&::placeholder': {
      color: '#9ca3af',
      opacity: 1,
    },
  },
  '& .MuiInputAdornment-root': {
    background: surfaceAuthCard,
    backgroundColor: surfaceAuthCard,
    mr: '8px',
  },
  '& input:-webkit-autofill, & input:-webkit-autofill:hover, & input:-webkit-autofill:focus': {
    WebkitBoxShadow: `0 0 0 100px ${surfaceAuthCard} inset`,
    WebkitTextFillColor: textPrimary,
    caretColor: textPrimary,
  },
} as const

export const webSurveyAccountPhoneInputSx = {
  ...webSurveyAccountInputSx,
  '& .MuiOutlinedInput-input, & .MuiInputBase-input': {
    px: '16px',
    py: '14px',
    color: 'rgba(15, 23, 42, 0.6)',
    WebkitTextFillColor: 'rgba(15, 23, 42, 0.6)',
    background: surfaceAuthCard,
    backgroundColor: surfaceAuthCard,
  },
  '&.Mui-disabled': {
    bgcolor: surfaceAuthCard,
    backgroundColor: surfaceAuthCard,
    backgroundImage: 'none',
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: borderInput,
  },
} as const

export const webSurveyAccountChecklistSx = {
  bgcolor: '#f9fafb',
  borderRadius: '10px',
  px: '12px',
  py: '8px',
  minHeight: 105,
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
  width: '100%',
  boxSizing: 'border-box',
} as const

export const webSurveyAccountChecklistTitleSx = {
  fontFamily: 'Inter, sans-serif',
  fontWeight: 500,
  fontSize: '12px',
  lineHeight: '16px',
  color: '#4a5565',
  m: 0,
} as const

export const webSurveyAccountChecklistItemSx = {
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  fontFamily: 'Inter, sans-serif',
  fontWeight: 400,
  fontSize: '12px',
  lineHeight: '16px',
  color: '#4a5565',
  m: 0,
} as const

export const webSurveyAccountChecklistMetItemSx = {
  ...webSurveyAccountChecklistItemSx,
  color: '#10B981',
  fontWeight: 500,
} as const

export const webSurveyAccountSubmitSx = {
  bgcolor: WEB_SURVEY_ACCOUNT_ORANGE,
  borderRadius: '12px',
  minHeight: 56,
  px: '24px',
  py: '16px',
  fontFamily: 'Inter, sans-serif',
  fontWeight: 600,
  fontSize: '18px',
  lineHeight: '24px',
  textTransform: 'none',
  color: WEB_SURVEY_ACCOUNT_NAVY,
  boxShadow: 'none',
  width: '100%',
  '&:hover': {
    bgcolor: '#EF773D',
    color: WEB_SURVEY_ACCOUNT_NAVY,
    boxShadow: 'none',
  },
  '&.Mui-disabled': {
    bgcolor: '#94a3b8',
    color: '#f8fafc',
  },
} as const

export const webSurveyAccountSignInRowSx = {
  fontFamily: 'Inter, sans-serif',
  fontWeight: 400,
  fontSize: '14px',
  lineHeight: '16px',
  letterSpacing: '-0.4304px',
  color: authDescriptionSlate,
  textAlign: 'center',
  width: '100%',
  m: 0,
} as const

export const webSurveyAccountSignInButtonSx = {
  fontFamily: 'Inter, sans-serif',
  fontWeight: 600,
  fontSize: '14px',
  lineHeight: '16px',
  letterSpacing: '-0.4304px',
  color: WEB_SURVEY_ACCOUNT_SIGN_IN,
  textTransform: 'none',
  minWidth: 0,
  p: 0,
  verticalAlign: 'baseline',
  '&:hover': {
    bgcolor: 'transparent',
    textDecoration: 'underline',
  },
} as const
